import React, { useState, useRef } from 'react';
import { Container, Row, Col, Button, Card, Alert, Spinner, Badge, ListGroup, Form } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import { downloadPdfReport, verifyBusinessName } from '../../api/bankParsingApi';
import { 
  createBankParsingJob, 
  getBankParsingJobStatus, 
  getBankParsingJobResult,
  getRecentBankParsingJobs 
} from '../../api/bankParsingJobsApi';
import { FaCloudUploadAlt, FaFileUpload, FaCheck, FaTimesCircle, FaFilePdf, FaHistory, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { getContactById } from '../../api/hubspotApi';
import BusinessNameVerificationModal from '../../components/BusinessNameVerificationModal';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const Analyze = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { success: notifySuccess, error: notifyError } = useNotifications();
  
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [activeDragZone, setActiveDragZone] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  // Application files (separate from bank statements)
  const [applicationFiles, setApplicationFiles] = useState([]);
  const [applicationDragZone, setApplicationDragZone] = useState(false);
  const applicationFileInputRef = useRef(null);
  
  // Notes for processor
  const [processorNotes, setProcessorNotes] = useState('');
  
  // Job management
  const [activeJobs, setActiveJobs] = useState(new Map()); // jobId -> { status, pollingInterval }
  const [recentJobs, setRecentJobs] = useState([]);
  const [expandedJobs, setExpandedJobs] = useState(new Set());
  const pollingIntervalsRef = useRef(new Map()); // Store polling intervals in ref to prevent cleanup issues
  const [jobResults, setJobResults] = useState(new Map()); // jobId -> results
  const fileInputRef = useRef(null);

  // Get contact ID from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const contactId = urlParams.get('id');
  const [contactName, setContactName] = React.useState(null);
  
  // Business name verification modal state
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(false);

  // Check if user is authenticated and load recent jobs
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    } else {
      loadRecentJobs();
    }
  }, [user, navigate]);

  // Fetch HubSpot contact name if contactId present
  React.useEffect(() => {
    const fetchContact = async () => {
      if (!contactId) return;
      try {
        // Pass 'bank-analysis' source to allow sales users to access contact data
        const contact = await getContactById(contactId, 'bank-analysis');
        const props = contact?.properties || {};
        const pickValue = (v) => {
          if (!v) return null;
          if (typeof v === 'string') return v;
          if (typeof v === 'object' && v.value !== undefined && v.value !== null) return String(v.value);
          return null;
        };
        const company = pickValue(props.company);
        const businessName = pickValue(props.business_name);
        const first = pickValue(props.firstname);
        const last = pickValue(props.lastname);
        const name = company || businessName || ([first, last].filter(Boolean).join(' ') || null);
        if (name) {
          setContactName(name);
          console.log(`[Analyze] Loaded contact name: ${name}`);
        }
      } catch (e) {
        console.warn('[Analyze] Failed to fetch HubSpot contact:', e?.message || e);
        setContactName(null);
      }
    };
    fetchContact();
  }, [contactId]);

  // Load recent jobs
  const loadRecentJobs = async () => {
    try {
      const jobs = await getRecentBankParsingJobs(20);
      setRecentJobs(jobs);
    } catch (error) {
      console.error('Failed to load recent jobs:', error);
    }
  };

  // Styles for the drop zone
  const dropZoneStyle = {
    border: '2px dashed #ccc',
    borderRadius: '8px',
    padding: '40px',
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: '#f8f9fa',
    transition: 'all 0.3s ease',
    minHeight: '200px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const activeDropZoneStyle = {
    ...dropZoneStyle,
    border: '2px dashed #0d6efd',
    backgroundColor: 'rgba(13, 110, 253, 0.05)'
  };

  const successDropZoneStyle = {
    ...dropZoneStyle,
    border: '2px dashed #198754',
    backgroundColor: 'rgba(25, 135, 84, 0.05)'
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    addFiles(files);
  };

  // Add files to the collection
  const addFiles = (files) => {
    
    // Filter for PDF files only
    const pdfFiles = files.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length !== files.length) {
      notifyError('Only PDF files are allowed');
    }
    
    if (pdfFiles.length > 0) {
      setSelectedFiles(prevFiles => [...prevFiles, ...pdfFiles]);
    }
  };

  // Remove a file
  const handleRemoveFile = (index) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    setActiveDragZone(true);
  };

  // Handle drag leave
  const handleDragLeave = (e) => {
    e.preventDefault();
    setActiveDragZone(false);
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    
    setActiveDragZone(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      addFiles(files);
    }
  };

  // Handle drop zone click
  const handleDropZoneClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Application file handlers
  const handleApplicationDragOver = (e) => {
    e.preventDefault();
    setApplicationDragZone(true);
  };

  const handleApplicationDragLeave = (e) => {
    e.preventDefault();
    setApplicationDragZone(false);
  };

  const handleApplicationDrop = (e) => {
    e.preventDefault();
    setApplicationDragZone(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      addApplicationFiles(files);
    }
  };

  const handleApplicationDropZoneClick = () => {
    if (applicationFileInputRef.current) {
      applicationFileInputRef.current.click();
    }
  };

  const addApplicationFiles = (files) => {
    const validFiles = files.filter(file => {
      // Allow common application file types
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png',
        'image/gif',
        'text/plain'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        notifyError(`File type not supported: ${file.name}`);
        return false;
      }
      
      // 10MB limit
      if (file.size > 10 * 1024 * 1024) {
        notifyError(`File too large: ${file.name} (max 10MB)`);
        return false;
      }
      
      return true;
    });

    if (validFiles.length > 0) {
      setApplicationFiles(prev => [...prev, ...validFiles]);
      notifySuccess(`Added ${validFiles.length} application file(s)`);
    }
  };

  const removeApplicationFile = (index) => {
    setApplicationFiles(prev => prev.filter((_, i) => i !== index));
  };


  // Handle analyze button click
  const handleAnalyze = async () => {
    if (selectedFiles.length === 0) {
      notifyError('Please select at least one bank statement');
      return;
    }

    if (selectedFiles.length > 12) {
      notifyError('Maximum 12 files allowed per job');
      return;
    }

    // Require application files when submitting from a contact
    if (contactId && applicationFiles.length === 0) {
      notifyError('Application file is required when submitting from a HubSpot contact');
      return;
    }

    // NEW: If coming from HubSpot contact, verify business name first
    if (contactId) {
      try {
        setVerificationLoading(true);
        setShowVerificationModal(true);
        
        // Pass ALL bank statement files for verification (to show statement organization)
        const result = await verifyBusinessName(selectedFiles, applicationFiles);
        
        setVerificationData({
          statementBusinessName: result.business_name,
          hubspotBusinessName: contactName, // Already loaded from HubSpot
          bankName: result.bank_name,
          statements: result.statements, // NEW: organized statements by account
          ssnWarning: result.ssn_warning // NEW: SSN warning
        });
        setVerificationLoading(false);
        
        // Modal is now showing with data, waiting for user to click Continue
        return; // Don't proceed with analysis yet
      } catch (error) {
        console.error('[Analyze] Business name verification failed:', error);
        setVerificationLoading(false);
        setShowVerificationModal(false);
        notifyError('Failed to verify business name. Please try again.');
        return;
      }
    }

    // If no contactId, proceed directly with analysis
    await proceedWithAnalysis();
  };

  // NEW: Separate function to proceed with analysis (called after verification or directly)
  const proceedWithAnalysis = async () => {
    try {
      // Close verification modal if open
      setShowVerificationModal(false);
      
      // Create FormData with both bank statements and application files (same as bank statements!)
      const formData = new FormData();
      
      // Add bank statements
      selectedFiles.forEach(file => {
        formData.append('statements', file);
      });
      
      // Add application files (exactly like bank statements - no separate upload!)
      applicationFiles.forEach(file => {
        formData.append('applicationFiles', file);
      });
      
      // Add processor notes if provided
      if (processorNotes.trim()) {
        formData.append('processorNotes', processorNotes.trim());
      }
      
      console.log(`[Analyze] Submitting job with ${selectedFiles.length} bank statements and ${applicationFiles.length} application files`);
      if (processorNotes.trim()) {
        console.log(`[Analyze] Including processor notes`);
      }
      
      // Create bank parsing job
      const jobResponse = await createBankParsingJob(formData, contactId);
      const jobId = jobResponse.jobId;
      const jobDetails = jobResponse.job; // New: get job details from response
      
      console.log('[Analyze] Job creation response:', jobResponse);
      console.log('[Analyze] Contact ID being sent:', contactId);
      console.log('[Analyze] Job details received:', jobDetails);
      
      notifySuccess(`Bank analysis job created: ${jobId}`);
      
      // Add to active jobs and start polling
      const pollingInterval = startPolling(jobId);
      pollingIntervalsRef.current.set(jobId, pollingInterval); // Store in ref
      const newJob = {
        status: jobDetails.status, 
        pollingInterval,
        filesCount: jobDetails.filesCount,
        createdAt: new Date(jobDetails.createdAt),
        hubspotContactId: jobDetails?.hubspotContactId || contactId || null,
        contactBusinessName: jobDetails?.contactBusinessName || null
      };
      
      setActiveJobs(prev => new Map(prev.set(jobId, newJob)));
      
      // Immediately add to recent jobs list (prepend to top) with contact business name
      const recentJob = {
        jobId: jobDetails?.jobId || jobId,
        status: jobDetails?.status || 'queued',
        filesCount: jobDetails?.filesCount || selectedFiles.length,
        createdAt: jobDetails?.createdAt ? new Date(jobDetails.createdAt) : new Date(),
        completedAt: null,
        hubspotContactId: jobDetails?.hubspotContactId || contactId,
        contactBusinessName: jobDetails?.contactBusinessName || null
      };
      
      setRecentJobs(prev => {
        // Remove if already exists, then prepend
        const filtered = prev.filter(job => job.jobId !== jobId);
        return [recentJob, ...filtered];
      });
      
      // Clear selected files, application files, and notes for next job
      setSelectedFiles([]);
      setApplicationFiles([]);
      setProcessorNotes('');
      if (fileInputRef.current) {
        try { fileInputRef.current.value = null; } catch {}
      }
      if (applicationFileInputRef.current) {
        try { applicationFileInputRef.current.value = null; } catch {}
      }
      
    } catch (error) {
      console.error('Failed to create job:', error);
      notifyError(`Failed to create job: ${error}`);
    }
  };

  // NEW: Handler for verification modal confirmation
  const handleVerificationConfirm = () => {
    proceedWithAnalysis();
  };

  // NEW: Handler for verification modal cancellation
  const handleVerificationCancel = () => {
    setShowVerificationModal(false);
    setVerificationData(null);
  };

  // Start polling for job status
  const startPolling = (jobId) => {
    console.log(`[UI] Starting polling for job ${jobId.slice(0, 8)}...`);
    const interval = setInterval(async () => {
      try {
        const status = await getBankParsingJobStatus(jobId);
        console.log(`[UI] Job ${jobId.slice(0, 8)}... status:`, status.status);
        
        console.log(`[UI] Polling result for ${jobId.slice(0, 8)}... - Status: ${status.status}`);
        
        // Update active job status and hydrate contact info if returned by status
        setActiveJobs(prev => {
          const updated = new Map(prev);
          const job = updated.get(jobId);
          if (job && job.status !== status.status) {
            console.log(`[UI] Updating active job ${jobId.slice(0, 8)}... from ${job.status} to ${status.status}`);
            updated.set(jobId, { 
              ...job, 
              status: status.status, 
              progress: status.progress,
              hubspotContactId: status.hubspotContactId ?? job.hubspotContactId,
              contactBusinessName: status.contactBusinessName ?? job.contactBusinessName
            });
            return updated;
          } else if (!job) {
            console.warn(`[UI] Job ${jobId.slice(0, 8)}... not found in active jobs`);
          }
          return prev; // Return previous state if no changes needed
        });
        
        // Also update the job status and contact info in recent jobs list
        setRecentJobs(prev => {
          const currentJob = prev.find(job => job.jobId === jobId);
          if (currentJob && currentJob.status !== status.status) {
            const updated = prev.map(job => 
              job.jobId === jobId 
                ? { 
                    ...job, 
                    status: status.status,
                    hubspotContactId: status.hubspotContactId ?? job.hubspotContactId,
                    contactBusinessName: status.contactBusinessName ?? job.contactBusinessName
                  }
                : job
            );
            console.log(`[UI] Updated recent jobs for ${jobId.slice(0, 8)}... from ${currentJob.status} to ${status.status}`);
            return updated;
          }
          return prev; // Return previous state if no changes needed
        });
        
        if (status.status === 'completed') {
          clearInterval(interval);
          pollingIntervalsRef.current.delete(jobId); // Remove from ref
          
          // Get results and cache them
          try {
            const result = await getBankParsingJobResult(jobId);
            setJobResults(prev => new Map(prev.set(jobId, result)));
            notifySuccess(`Job ${jobId.slice(0, 8)}... completed successfully!`);
          } catch (error) {
            console.error('Failed to get job results:', error);
            notifyError(`Failed to get results for job ${jobId.slice(0, 8)}...`);
          }
          
          // Remove from active jobs and get job info
          let activeJobInfo = null;
          setActiveJobs(prev => {
            const updated = new Map(prev);
            activeJobInfo = updated.get(jobId); // Get the job info before deleting
            updated.delete(jobId);
            return updated;
          });
          
          // Update recent jobs list with completed status
          setRecentJobs(prev => {
            const filtered = prev.filter(job => job.jobId !== jobId);
            const completedJob = {
              jobId,
              status: 'completed',
              filesCount: activeJobInfo?.filesCount || 1,
              createdAt: activeJobInfo?.createdAt || new Date(),
              completedAt: new Date()
            };
            return [completedJob, ...filtered];
          });
          
        } else if (status.status === 'failed') {
          clearInterval(interval);
          pollingIntervalsRef.current.delete(jobId); // Remove from ref
          notifyError(`Job ${jobId.slice(0, 8)}... failed: ${status.error || 'Unknown error'}`);
          
          // Remove from active jobs and get job info
          let activeJobInfo = null;
          setActiveJobs(prev => {
            const updated = new Map(prev);
            activeJobInfo = updated.get(jobId); // Get the job info before deleting
            updated.delete(jobId);
            return updated;
          });
          
          // Update recent jobs list with failed status
          setRecentJobs(prev => {
            const filtered = prev.filter(job => job.jobId !== jobId);
            const failedJob = {
              jobId,
              status: 'failed',
              filesCount: activeJobInfo?.filesCount || 1,
              createdAt: activeJobInfo?.createdAt || new Date(),
              completedAt: new Date()
            };
            return [failedJob, ...filtered];
          });
        }
      } catch (error) {
        console.error('Failed to get job status:', error);
        // Don't stop polling on temporary errors
      }
    }, 5000); // Poll every 5 seconds (optimized to reduce server load)
    
    return interval;
  };

  // Stop polling when component unmounts
  React.useEffect(() => {
    const intervals = pollingIntervalsRef.current;
    return () => {
      // Clear all active polling intervals on component unmount only
      intervals.forEach((interval, jobId) => {
        console.log(`[UI] Clearing polling interval for job ${jobId.slice(0, 8)}... on unmount`);
        clearInterval(interval);
      });
      intervals.clear();
    };
  }, []); // No dependencies needed since we're using ref

  // Toggle job expansion
  const toggleJobExpansion = async (jobId) => {
    const isExpanded = expandedJobs.has(jobId);
    
    if (isExpanded) {
      setExpandedJobs(prev => {
        const updated = new Set(prev);
        updated.delete(jobId);
        return updated;
      });
    } else {
      // Expand and load results if not cached
      if (!jobResults.has(jobId)) {
        try {
          const result = await getBankParsingJobResult(jobId);
          setJobResults(prev => new Map(prev.set(jobId, result)));
        } catch (error) {
          console.error('Failed to load job results:', error);
          notifyError('Failed to load job results');
          return;
        }
      }
      
      setExpandedJobs(prev => new Set(prev.add(jobId)));
    }
  };
  
  // Download PDF for a specific job
  const handleDownloadJobPdf = async (jobId) => {
    const results = jobResults.get(jobId);
    if (!results) {
      notifyError('No results available for PDF generation');
      return;
    }

    try {
      setGeneratingPdf(true);
      const result = await downloadPdfReport(results);
      notifySuccess(`PDF report downloaded: ${result.filename}`);
    } catch (error) {
      console.error('PDF download error:', error);
      notifyError(error || 'Failed to generate PDF report');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Clear all files
  const handleClearAll = () => {
    setSelectedFiles([]);
  };
  
  // Format job status badge
  const getStatusBadge = (status) => {
    const variants = {
      'queued': 'secondary',
      'processing': 'warning', 
      'completed': 'success',
      'failed': 'danger'
    };
    return variants[status] || 'secondary';
  };
  
  // Format date
  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric', 
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get current analysis results (for backward compatibility)
  const getCurrentResults = () => {
    // If there's only one expanded job, use its results
    if (expandedJobs.size === 1) {
      const jobId = Array.from(expandedJobs)[0];
      return jobResults.get(jobId);
    }
    return null;
  };
  
  const handleDownloadPdf = async () => {
    const results = getCurrentResults();
    if (!results) {
      notifyError('Please expand a completed job to download its PDF report');
      return;
    }

    try {
      setGeneratingPdf(true);
      const result = await downloadPdfReport(results);
      notifySuccess(`PDF report downloaded successfully: ${result.filename}`);
    } catch (error) {
      console.error('PDF download error:', error);
      notifyError(error || 'Failed to generate PDF report');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Get drop zone style based on state
  const getDropZoneStyle = () => {
    if (selectedFiles.length > 0) return successDropZoneStyle;
    if (activeDragZone) return activeDropZoneStyle;
    return dropZoneStyle;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helpers for account summaries UI (placeholders only for now)
  const monthLabelFromISO = (isoDate) => {
    try {
      const d = new Date(isoDate);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      }
    } catch {}
    return null;
  };

  const monthLabelFromMmYYYY = (mmYYYY) => {
    if (!mmYYYY || typeof mmYYYY !== 'string') return null;
    const [mm, yyyy] = mmYYYY.split('/');
    if (!mm || !yyyy) return null;
    const monthIndex = parseInt(mm, 10) - 1;
    if (isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) return null;
    const d = new Date(parseInt(yyyy, 10), monthIndex, 1);
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const deriveStatementMonthLabel = (statement, account) => {
    const start = statement?.statement_period?.start_date;
    const end = statement?.statement_period?.end_date;
    const txDate = statement?.transactions && statement.transactions.length > 0 ? statement.transactions[0].date : null;
    return (
      monthLabelFromISO(end) ||
      monthLabelFromISO(start) ||
      monthLabelFromISO(txDate) ||
      (account?.months_present && account.months_present.length === 1 ? monthLabelFromMmYYYY(account.months_present[0]) : null) ||
      'Unknown month'
    );
  };

  const groupResultsByAccount = (results) => {
    const map = new Map();
    if (!Array.isArray(results)) return map;
    results.forEach((r) => {
      const accounts = r?.data?.accounts || [];
      accounts.forEach((acc) => {
        const key = acc?.account_last4 || 'N/A';
        const existing = map.get(key) || { 
          accountLast4: acc?.account_last4 || null, 
          accountType: acc?.account_type || null,
          bankName: acc?.bank_name || null,
          accountHolder: acc?.account_holder || null,
          statements: [] 
        };
        // Each account is now a single statement (no nested statements array)
        existing.statements.push({
          label: deriveStatementMonthLabel(acc, acc),
          raw: acc
        });
        map.set(key, existing);
      });
    });

    // Sort statements by end_date/start_date when available (most recent first)
    map.forEach((val) => {
      val.statements.sort((a, b) => {
        const ad = new Date(a?.raw?.statement_period?.end_date || a?.raw?.statement_period?.start_date || '1970-01-01').getTime();
        const bd = new Date(b?.raw?.statement_period?.end_date || b?.raw?.statement_period?.start_date || '1970-01-01').getTime();
        return bd - ad; // Reversed: most recent (larger date) first
      });
    });

    return map;
  };

  // KPIs for a single account group
  const computeAccountKpis = (group) => {
    const statements = group?.statements || [];
    const pluck = (selector) => statements
      .map(selector)
      .filter((v) => typeof v === 'number');
    const avg = (arr) => arr.length ? Math.round((arr.reduce((a,b)=>a+b,0)/arr.length)*100)/100 : null;
    return {
      statements: statements.length,
      avgDeposits: avg(pluck((s) => s.raw?.metrics?.deposits)),
      avgWithdrawals: avg(pluck((s) => s.raw?.metrics?.withdrawals)),
      avgDailyBalance: avg(pluck((s) => s.raw?.metrics?.averageDailyBalance)),
      avgNegativeDays: avg(pluck((s) => s.raw?.metrics?.negativeDays)),
      avgNsfs: avg(pluck((s) => s.raw?.metrics?.nsfs)),
      avgDepositCount: avg(pluck((s) => s.raw?.metrics?.depositCount)),
      avgWithdrawalCount: avg(pluck((s) => s.raw?.metrics?.withdrawalCount))
    };
  };

  const buildAccountChartData = (group) => {
    const labels = group.statements.map((s) => s.label);
    const deposits = group.statements.map((s) => typeof s.raw?.metrics?.deposits === 'number' ? s.raw.metrics.deposits : 0);
    const withdrawals = group.statements.map((s) => typeof s.raw?.metrics?.withdrawals === 'number' ? Math.abs(s.raw.metrics.withdrawals) : 0);
    return {
      labels,
      datasets: [
        {
          label: 'Deposits',
          backgroundColor: 'rgba(46, 62, 159, 0.7)',
          borderColor: 'rgba(46, 62, 159, 1)',
          borderWidth: 1,
          data: deposits
        },
        {
          label: 'Withdrawals',
          backgroundColor: 'rgba(108, 117, 125, 0.6)',
          borderColor: 'rgba(108, 117, 125, 1)',
          borderWidth: 1,
          data: withdrawals
        }
      ]
    };
  };

  const accountChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: { mode: 'index', intersect: false }
    },
    scales: {
      x: { stacked: false },
      y: { beginAtZero: true }
    }
  };


  return (
    <>
      <style>{`
        .hover-bg-light:hover {
          background-color: #f8f9fa !important;
        }
        .cursor-pointer {
          cursor: pointer;
        }
      `}</style>
      <Container className="pt-4">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2>Analyze Bank Statements</h2>
              <p className="text-muted mb-0">Upload bank statements to extract financial data</p>
            </div>
            <div>
              <Button 
                variant="success" 
                onClick={handleAnalyze}
                disabled={selectedFiles.length === 0}
                className="me-2"
              >
                <FaFileUpload className="me-2" />
                Analyze ({selectedFiles.length} files)
              </Button>
              
              {selectedFiles.length > 0 && (
                <Button 
                  variant="outline-secondary" 
                  onClick={handleClearAll}
                >
                  Clear All
                </Button>
              )}
              
              {getCurrentResults() && (
                <Button 
                  variant="success"
                  onClick={handleDownloadPdf}
                  disabled={generatingPdf}
                  className="ms-2"
                >
                  {generatingPdf ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <FaFilePdf className="me-2" />
                      Download PDF Report
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Contact Info Header - Only visible when contactId is present */}
      {contactId && contactName && (
        <Row>
          <Col>
            <div className="mb-3">
              <h4 className="mb-1">{contactName}</h4>
              <small className="text-muted">
                HubSpot ID: <Badge bg="secondary">{contactId}</Badge>
              </small>
            </div>
          </Col>
        </Row>
      )}

      {/* Processor Notes Section */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Card.Title className="d-flex align-items-center mb-3">
                Notes for Processor
              </Card.Title>
              <Form.Group>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Add any notes or details about this deal for the processor (optional)..."
                  value={processorNotes}
                  onChange={(e) => setProcessorNotes(e.target.value)}
                  style={{ resize: 'vertical' }}
                />
                <Form.Text className="text-muted">
                  These notes will be included in the email sent to the processor
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Upload Section */}
      <Row>
        {/* Bank Statements Upload */}
        <Col md={contactId ? 6 : 12}>
          <Card className="h-100">
            <Card.Body>
              <Card.Title className="d-flex align-items-center">
                <FaCloudUploadAlt className="me-2" />
                Upload Bank Statements
                <Badge 
                  bg={selectedFiles.length > 0 ? "success" : "secondary"} 
                  className="ms-2"
                >
                  {selectedFiles.length} files selected
                </Badge>
              </Card.Title>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="application/pdf"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />

                {/* Drop zone */}
                <div
                  style={getDropZoneStyle()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleDropZoneClick}
                  className={`mb-3 ${activeDragZone ? 'border-primary' : ''}`}
                >
                  <FaCloudUploadAlt 
                    size={48} 
                    className="mb-3" 
                    color={selectedFiles.length > 0 ? "#198754" : "#0d6efd"} 
                  />
                  <h5>
                    {selectedFiles.length > 0 
                      ? "Add More Bank Statements" 
                      : "Upload Bank Statements"}
                  </h5>
                  <p className="text-muted mb-2">
                    Drag & drop PDF files here or click to browse
                  </p>
                  <small className="text-muted">
                    Only PDF files are accepted • Maximum 20 files • 25MB per file
                  </small>
                </div>

                {/* File list */}
                {selectedFiles.length > 0 && (
                  <div className="mt-2 selected-files-compact">
                    <div className="d-flex align-items-center mb-1 small">
                      <span className="fw-semibold">Selected Files</span>
                      <span className="text-muted ms-1">({selectedFiles.length})</span>
                    </div>
                    <ListGroup className="small">
                      {selectedFiles.map((file, index) => (
                        <ListGroup.Item 
                          key={index} 
                          className="d-flex justify-content-between align-items-center py-1"
                        >
                          <div className="d-flex align-items-center flex-grow-1">
                            <FaCheck size={10} className="text-success me-1" />
                            <div className="flex-grow-1">
                              <div className="fw-semibold">
                                {file.name}
                              </div>
                              <small className="text-muted file-meta">
                                {formatFileSize(file.size)}
                              </small>
                            </div>
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveFile(index)}
                            className="p-0 remove-btn ms-2"
                          >
                            <FaTimesCircle size={12} />
                          </Button>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                )}

                {selectedFiles.length === 0 && (
                  <Alert variant="warning" className="mt-3">
                    <strong>No files selected.</strong> Please upload at least one bank statement PDF to proceed with analysis.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>

        {/* Application Files Upload - Only visible when contactId is present */}
        {contactId && (
          <Col md={6}>
            <Card className="h-100">
              <Card.Body>
                <Card.Title className="d-flex align-items-center">
                  <FaFileUpload className="me-2" />
                  Upload Application Files
                  <Badge bg="danger" className="ms-2">
                    REQUIRED
                  </Badge>
                  <Badge 
                    bg={applicationFiles.length > 0 ? "success" : "secondary"} 
                    className="ms-2"
                  >
                    {applicationFiles.length} files selected
                  </Badge>
                </Card.Title>

                  {/* Application File Drop Zone */}
                  <div
                    style={{
                      border: `2px dashed ${applicationDragZone ? '#0d6efd' : '#ccc'}`,
                      borderRadius: '8px',
                      padding: '40px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: applicationDragZone ? 'rgba(13, 110, 253, 0.05)' : '#f8f9fa',
                      transition: 'all 0.3s ease',
                      minHeight: '200px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                    onDragOver={handleApplicationDragOver}
                    onDragLeave={handleApplicationDragLeave}
                    onDrop={handleApplicationDrop}
                    onClick={handleApplicationDropZoneClick}
                    className={`mb-3 ${applicationDragZone ? 'border-primary' : ''}`}
                  >
                    <FaCloudUploadAlt
                      size={48}
                      className="mb-3"
                      color={applicationFiles.length > 0 ? "#198754" : "#0d6efd"}
                    />
                    <h5>
                      {applicationFiles.length > 0
                        ? "Add More Application Files"
                        : "Upload Application Files"}
                    </h5>
                    <p className="text-muted mb-2">
                      Drag & drop files here or click to browse
                    </p>
                    <small className="text-muted">
                      Supported: PDF, Word, Images (max 10MB each)
                    </small>
                  </div>

                  {/* Hidden file input for application files */}
                  <input
                    type="file"
                    ref={applicationFileInputRef}
                    onChange={(e) => {
                      if (e.target.files) {
                        addApplicationFiles(Array.from(e.target.files));
                      }
                    }}
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt"
                    style={{ display: 'none' }}
                  />

                  {/* Application Files List */}
                  {applicationFiles.length > 0 && (
                    <div>
                      <h6 className="mb-2">Selected Application Files:</h6>
                      <ListGroup>
                        {applicationFiles.map((file, index) => (
                          <ListGroup.Item
                            key={index}
                            className="d-flex justify-content-between align-items-center"
                          >
                            <div className="d-flex align-items-center">
                              <FaFilePdf className="me-2 text-danger" />
                              <div>
                                <div className="fw-medium">{file.name}</div>
                                <small className="text-muted">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </small>
                              </div>
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeApplicationFile(index)}
                              className="p-0 remove-btn"
                            >
                              <FaTimesCircle size={12} />
                            </Button>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                      
                      {/* Note: Application files will be uploaded when job processes */}
                      <div className="mt-3">
                        <Alert variant="info" className="mb-0">
                          <small>
                            <strong>Note:</strong> Application files will be uploaded to HubSpot and attached to the processor email when the bank analysis job processes.
                          </small>
                        </Alert>
                      </div>
                    </div>
                  )}

                  {applicationFiles.length === 0 && (
                    <Alert variant="warning" className="mt-3">
                      <strong>⚠ No application files selected.</strong> Application files are required to analyze bank statements from a HubSpot contact.
                    </Alert>
                  )}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
      
      {/* Active Jobs */}
      {activeJobs.size > 0 && (
        <Row className="mt-4">
          <Col>
            <Card>
              <Card.Header className="bg-light">
                <h5 className="mb-0">Active Jobs</h5>
              </Card.Header>
              <Card.Body>
                {Array.from(activeJobs.entries()).map(([jobId, job]) => (
                  <div key={jobId} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        {job.status === 'completed' ? (
                          <FaCheck className="text-success" size={16} />
                        ) : job.status === 'failed' ? (
                          <FaTimesCircle className="text-danger" size={16} />
                        ) : (
                          <Spinner animation="border" size="sm" />
                        )}
                      </div>
                      <div>
                        <div className="fw-semibold">{job.contactBusinessName || jobId.slice(0, 8) + '...'}</div>
                        <small className="text-muted">{job.filesCount} files • {formatDate(job.createdAt)}</small>
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <Badge bg={getStatusBadge(job.status)} className="me-2">
                        {job.status}
                      </Badge>
                      {job.progress && (
                        <small className="text-muted">
                          {job.progress.current}/{job.progress.total}
                        </small>
                      )}
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      
      {/* Job History */}
      {recentJobs.length > 0 && (
        <Row className="mt-4">
          <Col>
            <Card>
              <Card.Header className="bg-light d-flex align-items-center">
                <FaHistory className="me-2" />
                <h5 className="mb-0">Recent Jobs</h5>
              </Card.Header>
              <Card.Body className="p-0">
                {recentJobs.map((job) => (
                  <div key={job.jobId}>
                    <div 
                      className="d-flex align-items-center justify-content-between p-3 border-bottom cursor-pointer hover-bg-light"
                      onClick={() => job.status === 'completed' && toggleJobExpansion(job.jobId)}
                      style={{ cursor: job.status === 'completed' ? 'pointer' : 'default' }}
                    >
                      <div className="d-flex align-items-center">
                        <div className="me-3">
                          {job.status === 'completed' ? (
                            expandedJobs.has(job.jobId) ? (
                              <FaChevronDown className="text-primary" size={14} />
                            ) : (
                              <FaChevronRight className="text-primary" size={14} />
                            )
                          ) : job.status === 'failed' ? (
                            <FaTimesCircle className="text-danger" size={16} />
                          ) : (
                            <Spinner animation="border" size="sm" />
                          )}
                        </div>
                        <div>
                          <div className="fw-semibold">
                            {job.contactBusinessName || `Job ${job.jobId.slice(0, 8)}...`}
                          </div>
                          <small className="text-muted">
                            {job.contactBusinessName && (
                              <>ID: {job.jobId.slice(0, 8)}... • </>
                            )}
                            {job.filesCount} files • {formatDate(job.createdAt)}
                            {job.completedAt && ` → ${formatDate(job.completedAt)}`}
                          </small>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <Badge bg={getStatusBadge(job.status)}>
                          {job.status}
                        </Badge>
                        {job.status === 'completed' && expandedJobs.has(job.jobId) && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="ms-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadJobPdf(job.jobId);
                            }}
                            disabled={generatingPdf}
                          >
                            <FaFilePdf className="me-1" />
                            PDF
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded Job Results */}
                    {expandedJobs.has(job.jobId) && jobResults.has(job.jobId) && (
                      <div className="p-3 bg-light border-bottom">
                        {(() => {
                          const results = jobResults.get(job.jobId);
                          const grouped = groupResultsByAccount(results?.results || []);
                          return grouped.size > 0 ? (
                            Array.from(grouped.entries()).map(([last4, group]) => (
                              <div key={last4} className="mb-4">
                                <h6 className="mb-3">
                                  {group.accountType && group.accountType !== 'null' ? (
                                    <span className="text-capitalize">{group.accountType}</span>
                                  ) : (
                                    <span>Account</span>
                                  )}
                                  {' ending in '}
                                  {last4 || 'N/A'}
                                  {group.bankName && (
                                    <small className="text-muted ms-2">({group.bankName})</small>
                                  )}
                                </h6>
                                {/* Account-level KPI mini cards */}
                                <Row className="g-2 mb-3">
                                  {(() => {
                                    const k = computeAccountKpis(group);
                                    const items = [
                                      { label: 'Statements', value: k.statements ?? '—' },
                                      { label: 'Avg Deposits', value: typeof k.avgDeposits === 'number' ? `$${k.avgDeposits.toLocaleString()}` : '—' },
                                      { label: 'Avg Deposit Count', value: typeof k.avgDepositCount === 'number' ? Math.round(k.avgDepositCount) : '—' },
                                      { label: 'Avg Withdrawals', value: typeof k.avgWithdrawals === 'number' ? `$${k.avgWithdrawals.toLocaleString()}` : '—' },
                                      { label: 'Avg Withdrawal Count', value: typeof k.avgWithdrawalCount === 'number' ? Math.round(k.avgWithdrawalCount) : '—' },
                                      { label: 'Avg Daily Balance', value: typeof k.avgDailyBalance === 'number' ? `$${k.avgDailyBalance.toLocaleString()}` : '—' },
                                      { label: 'Avg Negative Days', value: typeof k.avgNegativeDays === 'number' ? k.avgNegativeDays : '—' },
                                      { label: 'Avg NSFs', value: typeof k.avgNsfs === 'number' ? k.avgNsfs : '—' }
                                    ];
                                    return items.map((it, idx) => (
                                      <Col key={idx} xs={6} md={4} lg={2}>
                                        <Card className="dashboard-kpi h-100">
                                          <Card.Body className="py-2">
                                            <div className="kpi-value">{it.value}</div>
                                            <div className="kpi-label text-muted">{it.label}</div>
                                          </Card.Body>
                                        </Card>
                                      </Col>
                                    ));
                                  })()
                                  }
                                </Row>
                                {/* Bar chart for deposits vs withdrawals per statement */}
                                <div style={{ height: 240 }} className="mb-3">
                                  <Bar data={buildAccountChartData(group)} options={accountChartOptions} />
                                </div>
                                
                                {/* Per-statement details */}
                                <div className="mt-3">
                                  <h6 className="mb-2">Statements</h6>
                                  <div className="table-responsive">
                                    <table className="table table-sm align-middle">
                                      <thead>
                                        <tr>
                                          <th>Period</th>
                                          <th>Opening</th>
                                          <th>Deposits</th>
                                          <th># Deposits</th>
                                          <th>Withdrawals</th>
                                          <th># Withdrawals</th>
                                          <th>Avg Daily Bal.</th>
                                          <th>Closing</th>
                                          <th>NSFs</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(group.statements || []).map((s, idx) => {
                                          const st = s.raw || {};
                                          const metrics = st.metrics || {};
                                          const period = st.statement_period || {};
                                          const label = s.label || (period.start_date && period.end_date ? `${period.start_date} → ${period.end_date}` : '—');
                                          const opening = typeof st.opening_balance === 'number' ? `$${st.opening_balance.toLocaleString()}` : '—';
                                          const deposits = typeof metrics.deposits === 'number' ? `$${metrics.deposits.toLocaleString()}` : '—';
                                          const depositCount = typeof metrics.depositCount === 'number' ? metrics.depositCount : '—';
                                          const withdrawals = typeof metrics.withdrawals === 'number' ? `$${Math.abs(metrics.withdrawals).toLocaleString()}` : '—';
                                          const withdrawalCount = typeof metrics.withdrawalCount === 'number' ? metrics.withdrawalCount : '—';
                                          const closing = typeof st.closing_balance === 'number' ? `$${st.closing_balance.toLocaleString()}` : '—';
                                          const adb = typeof metrics.averageDailyBalance === 'number' ? `$${metrics.averageDailyBalance.toLocaleString()}` : '—';
                                          const nsfs = typeof metrics.nsfs === 'number' ? metrics.nsfs : '—';
                                          return (
                                            <tr key={idx}>
                                              <td>{label}</td>
                                              <td>{opening}</td>
                                              <td>{deposits}</td>
                                              <td>{depositCount}</td>
                                              <td>{withdrawals}</td>
                                              <td>{withdrawalCount}</td>
                                              <td>{adb}</td>
                                              <td>{closing}</td>
                                              <td>{nsfs}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>

                                {/* Competitor Findings */}
                                {/* Competitor Payments - Now organized by statement period */}
                                <div className="mt-4">
                                  <h6 className="mb-3">Potential Competitor Payments</h6>
                                  
                                  {group.statements.map((stmt, stmtIdx) => {
                                    const competitorFindings = stmt.raw?.competitorFindings || [];
                                    const period = stmt.raw?.statement_period || {};
                                    
                                    return (
                                      <div key={stmtIdx} className="mb-4">
                                        <div className="d-flex align-items-center mb-2">
                                          <strong className="me-2">{stmt.label}</strong>
                                          {period.start_date && period.end_date && (
                                            <Badge bg="light" text="dark" className="font-monospace" style={{ fontSize: '0.75rem' }}>
                                              {period.start_date} → {period.end_date}
                                            </Badge>
                                          )}
                                        </div>
                                        
                                        {competitorFindings.length === 0 ? (
                                          <p className="text-muted small ms-3">No competitor payments in this period</p>
                                        ) : (
                                          <div className="table-responsive">
                                            <table className="table table-sm table-bordered">
                                              <thead className="table-light">
                                                <tr>
                                                  <th>Lender</th>
                                                  <th>Frequency</th>
                                                  <th>Occurrences</th>
                                                  <th>Amount</th>
                                                  <th>Confidence</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {competitorFindings.map((finding, idx) => (
                                                  <tr key={idx}>
                                                    <td>
                                                      <div>
                                                        <strong>{finding.matchedLender || 'Unknown'}</strong>
                                                      </div>
                                                      <small className="text-muted">
                                                        {finding.matchedAlias}
                                                      </small>
                                                    </td>
                                                    <td>
                                                      <span className={`badge ${
                                                        finding.frequency === 'daily' ? 'bg-primary' : 
                                                        finding.frequency === 'weekly' ? 'bg-secondary' : 
                                                        'bg-info'
                                                      }`}>
                                                        {finding.frequency === 'daily' ? 'Daily' : 
                                                         finding.frequency === 'weekly' ? 'Weekly' : 
                                                         finding.frequency || 'Irregular'}
                                                      </span>
                                                    </td>
                                                    <td>{finding.occurrences}</td>
                                                    <td>
                                                      {finding.amount ? `$${finding.amount.toLocaleString()}` : '—'}
                                                      {finding.amountRange && (
                                                        <div>
                                                          <small className="text-muted">
                                                            ${finding.amountRange.min.toLocaleString()} - ${finding.amountRange.max.toLocaleString()}
                                                          </small>
                                                        </div>
                                                      )}
                                                    </td>
                                                    <td>
                                                      <span className={`badge ${
                                                        finding.confidence >= 0.9 ? 'bg-success' : 
                                                        finding.confidence >= 0.7 ? 'bg-warning' : 
                                                        'bg-danger'
                                                      }`}>
                                                        {Math.round(finding.confidence * 100)}%
                                                      </span>
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-muted">No account data available</div>
                          );
                        })()
                        }
                      </div>
                    )}
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      </Container>

      {/* Business Name Verification Modal (only shown when coming from HubSpot) */}
      <BusinessNameVerificationModal
        show={showVerificationModal}
        onConfirm={handleVerificationConfirm}
        onCancel={handleVerificationCancel}
        hubspotBusinessName={verificationData?.hubspotBusinessName}
        statementBusinessName={verificationData?.statementBusinessName}
        statements={verificationData?.statements}
        ssnWarning={verificationData?.ssnWarning}
        loading={verificationLoading}
      />
    </>
  );
};

export default Analyze;
